// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CampaignNewChatDraft.test.tsx
import { ArrowUp, CircleNotch } from "@phosphor-icons/react";
import { Button, Flex, Text, TextArea } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { InputPlusMenu } from "./InputPlusMenu";

import { useAttachedFiles } from "../hooks/useAttachedFiles";

type DraftAttachment = {
  key: string;
  source: string;
  type: string;
  name?: string;
};

/**
 * Blank private-chat surface shown when the user starts a new personal chat
 * inside a project. Renders the empty-thread affordance and a composer that
 * creates the chat on first send (the API requires an initial message).
 */
export function CampaignNewChatDraft({
  onSubmit,
  isSubmitting = false,
  "data-testid": dataTestId = "campaign-new-chat-draft",
}: {
  onSubmit: (message: string, attachments?: DraftAttachment[]) => void;
  isSubmitting?: boolean;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");
  const [text, setText] = useState("");
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles();

  const trimmed = text.trim();
  const hasContent = trimmed.length > 0 || readyAttachments.length > 0;
  const canSubmit = hasContent && !isSubmitting && !hasPending;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(
      trimmed,
      readyAttachments.length > 0 ? readyAttachments : undefined,
    );
    setText("");
    clearAll();
  };

  return (
    <div
      className="ask-puntt-surface flex min-h-0 flex-1 flex-col"
      data-testid={dataTestId}
    >
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-8">
        <p
          className="text-center text-[11px] font-medium uppercase tracking-[0.08em] text-puntt-neutral-gray-9"
          data-testid="campaign-new-chat-empty-label"
        >
          {t("campaignNewChatDraft.emptyLabel")}
        </p>
      </div>

      <div
        className="shrink-0 border-t border-puntt-neutral-gray-6 p-4"
        data-testid="campaign-new-chat-composer-root"
      >
        <div className="mx-auto max-w-[720px]">
          <div className="relative overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20">
            <TextArea
              data-testid="campaign-new-chat-composer"
              aria-label={t("campaignNewChatDraft.composerAriaLabel")}
              size="2"
              variant="surface"
              placeholder={t("campaignNewChatDraft.composerPlaceholder")}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              className="min-h-[72px] w-full resize-none border-none !shadow-none"
              style={{ outline: "none", boxShadow: "none" }}
            />

            {attachedItems.length > 0 && (
              <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                {attachedItems.map((item, idx) => (
                  <Flex
                    key={item.id}
                    align="center"
                    gap="1"
                    className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                    data-testid="campaign-new-chat-attached-file"
                  >
                    {item.status === "pending" && (
                      <CircleNotch
                        size={12}
                        weight="bold"
                        className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                      />
                    )}
                    <Text
                      size="1"
                      className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                    >
                      {item.file.name}
                      {item.status === "error" &&
                        ` ${t("campaignNewChatDraft.attachmentFailedSuffix")}`}
                    </Text>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                      aria-label={t(
                        "campaignNewChatDraft.removeAttachmentAriaLabel",
                        {
                          fileName: item.file.name,
                        },
                      )}
                      data-testid="campaign-new-chat-remove-attachment"
                    >
                      ×
                    </button>
                  </Flex>
                ))}
              </Flex>
            )}

            <Flex justify="between" align="end" className="p-1">
              <InputPlusMenu
                iconSize={16}
                onFilesSelected={addFiles}
                onScreenshotCaptured={addFile}
              />
              <Button
                data-testid="campaign-new-chat-send"
                size="2"
                className="cursor-pointer rounded-xl"
                disabled={!canSubmit}
                onClick={submit}
                aria-label={t("campaignNewChatDraft.sendButtonAriaLabel")}
              >
                {isSubmitting || hasPending ? (
                  <CircleNotch
                    size={14}
                    weight="bold"
                    className="animate-spin"
                  />
                ) : (
                  <ArrowUp size={14} weight="bold" />
                )}
              </Button>
            </Flex>
          </div>
        </div>
      </div>
    </div>
  );
}
